import type { ReactNode } from "react";
import { differenceInCalendarDays, format, formatDistanceToNow, parseISO } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { Popover, Tag, Typography } from "antd";

import { ExpiryType } from "@src/appV2/Requirement/constants";
import { DOCUMENT_STATUS_METADATA } from "../../../constants/documents";

interface ExpiryTagInfo {
  color?: string;
  content: string;
  popoverContent?: string;
}

type DocumentStatusKey = keyof typeof DOCUMENT_STATUS_METADATA;

function isDocumentStatusKey(status: string): status is DocumentStatusKey {
  return status in DOCUMENT_STATUS_METADATA;
}

const { Text } = Typography;

export function formatCreatedAt(date?: string, timeZone?: string): string | null {
  if (!date) {
    return null;
  }

  const parsedDate = parseISO(date);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  const zonedDate = timeZone ? utcToZonedTime(parsedDate, timeZone) : parsedDate;
  return format(zonedDate, "MM/dd/yy hh:mm a");
}

export function getExpiryTagInfo(expiry?: string): ExpiryTagInfo {
  if (!expiry) {
    return {
      content: "No Expiry",
      popoverContent: "Expiry not set",
    };
  }

  const now = new Date();
  const expiryDate = new Date(expiry);
  if (Number.isNaN(expiryDate.getTime())) {
    return {
      content: "Invalid date",
    };
  }

  if (expiryDate.getTime() <= now.getTime()) {
    return {
      color: "red",
      content: formatDistanceToNow(expiryDate, { addSuffix: true }),
      popoverContent: format(expiryDate, "dd MMM, yyyy"),
    };
  }

  const days = differenceInCalendarDays(expiryDate, now);
  return {
    color: days > 30 ? "blue" : "gold",
    content: formatDistanceToNow(expiryDate, { addSuffix: true }),
    popoverContent: format(expiryDate, "dd MMM, yyyy"),
  };
}

interface RenderExpiryParams {
  expiryType?: string;
  expiryDate?: string;
  wrapWithPopover?: boolean;
}

export function renderExpiryCell({
  expiryType,
  expiryDate,
  wrapWithPopover = true,
}: RenderExpiryParams): ReactNode {
  if (expiryType === ExpiryType.DOES_NOT_EXPIRE) {
    return <Text aria-label="Document expiry">Does not expire</Text>;
  }

  const { color, content, popoverContent } = getExpiryTagInfo(expiryDate);
  const tag = (
    <Tag color={color} aria-label="Document expiry">
      {content}
    </Tag>
  );

  if (wrapWithPopover && popoverContent) {
    return <Popover content={popoverContent}>{tag}</Popover>;
  }

  return tag;
}

export function renderStatusCell(status?: string): ReactNode {
  if (!status) {
    return <Tag>unknown</Tag>;
  }
  if (!isDocumentStatusKey(status)) {
    return <Tag>{status.toLowerCase()}</Tag>;
  }
  const metadata = DOCUMENT_STATUS_METADATA[status];
  const { color, content } = metadata;
  return <Tag color={color}>{content}</Tag>;
}
